import React, { useState, useEffect } from 'react';

/**
 * Props interface for ErrorNotification component
 */
interface ErrorNotificationProps {
  // Error details
  error: Error | string | null;
  
  // Callback functions
  onRetry?: () => void;
  onDismiss?: () => void;
  
  // Customization options
  type?: 'error' | 'warning' | 'info';
  title?: string;
  retryText?: string;
  dismissText?: string;
  
  // Behavior options
  autoHide?: boolean;
  autoHideDelay?: number;
  retryable?: boolean;
  dismissible?: boolean;
  
  // Styling options
  position?: 'top' | 'bottom' | 'inline';
  className?: string;
}

/**
 * ErrorNotification Component
 * 
 * Displays inline error notifications with retry and dismiss functionality.
 * Features:
 * - Multiple error types (error, warning, info)
 * - Retry functionality for recoverable errors
 * - Auto-hide capability
 * - Customizable styling and positioning
 * - Accessible design with proper ARIA labels
 * 
 * This component provides user-friendly error feedback for the FillyOnIris workflow.
 */
export const ErrorNotification: React.FC<ErrorNotificationProps> = ({
  error,
  onRetry,
  onDismiss,
  type = 'error',
  title,
  retryText = 'Try Again',
  dismissText = 'Dismiss',
  autoHide = false,
  autoHideDelay = 5000,
  retryable = true,
  dismissible = true,
  position = 'inline',
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);

  // Auto-hide functionality
  useEffect(() => {
    if (autoHide && error && isVisible) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoHideDelay);

      return () => clearTimeout(timer);
    }
  }, [error, isVisible, autoHide, autoHideDelay]);

  // Reset visibility when error changes
  useEffect(() => {
    if (error) {
      setIsVisible(true);
      setIsRetrying(false);
    }
  }, [error]);

  /**
   * Handles retry action with loading state
   */
  const handleRetry = async () => {
    if (!onRetry || isRetrying) return;

    console.log('🔄 ERROR NOTIFICATION: User initiated retry');
    setIsRetrying(true);

    try {
      await onRetry();
    } catch (retryError) {
      console.error('❌ ERROR NOTIFICATION: Retry failed:', retryError);
    } finally {
      setIsRetrying(false);
    }
  };

  /**
   * Handles dismiss action
   */
  const handleDismiss = () => {
    console.log('❌ ERROR NOTIFICATION: User dismissed notification');
    setIsVisible(false);
    
    if (onDismiss) {
      onDismiss();
    }
  };

  // Don't render if no error or not visible
  if (!error || !isVisible) {
    return null;
  }

  // Extract error message
  const errorMessage = typeof error === 'string' ? error : error.message;
  const errorName = typeof error === 'object' && error.name ? error.name : 'Error';

  // Determine styling based on type
  const getTypeStyles = () => {
    switch (type) {
      case 'warning':
        return {
          backgroundColor: '#fff3cd',
          borderColor: '#ffeaa7',
          color: '#856404',
          iconColor: '#f39c12'
        };
      case 'info':
        return {
          backgroundColor: '#d1ecf1',
          borderColor: '#bee5eb',
          color: '#0c5460',
          iconColor: '#17a2b8'
        };
      case 'error':
      default:
        return {
          backgroundColor: '#f8d7da',
          borderColor: '#f5c6cb',
          color: '#721c24',
          iconColor: '#dc3545'
        };
    }
  };

  const typeStyles = getTypeStyles();

  // Determine icon based on type
  const getIcon = () => {
    switch (type) {
      case 'warning':
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" 
            />
          </svg>
        );
      case 'info':
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
            />
          </svg>
        );
      case 'error':
      default:
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
            />
          </svg>
        );
    }
  };

  // Position-specific styles
  const getPositionStyles = () => {
    const baseStyles = {
      padding: '1rem',
      border: `1px solid ${typeStyles.borderColor}`,
      borderRadius: '6px',
      backgroundColor: typeStyles.backgroundColor,
      color: typeStyles.color,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '0.875rem',
      lineHeight: '1.5'
    };

    switch (position) {
      case 'top':
        return {
          ...baseStyles,
          position: 'fixed' as const,
          top: '1rem',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          minWidth: '300px',
          maxWidth: '500px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        };
      case 'bottom':
        return {
          ...baseStyles,
          position: 'fixed' as const,
          bottom: '1rem',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          minWidth: '300px',
          maxWidth: '500px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        };
      case 'inline':
      default:
        return {
          ...baseStyles,
          margin: '1rem 0'
        };
    }
  };

  return (
    <div 
      className={`error-notification error-notification-${type} ${className}`}
      style={getPositionStyles()}
      role="alert"
      aria-live="polite"
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        {/* Icon */}
        <div style={{ 
          color: typeStyles.iconColor, 
          flexShrink: 0,
          marginTop: '0.125rem'
        }}>
          {getIcon()}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title */}
          {title && (
            <h4 style={{ 
              margin: '0 0 0.5rem 0', 
              fontSize: '1rem', 
              fontWeight: '600' 
            }}>
              {title}
            </h4>
          )}

          {/* Error Message */}
          <div style={{ marginBottom: '0.75rem' }}>
            {process.env.NODE_ENV === 'development' && errorName !== 'Error' && (
              <strong style={{ display: 'block', marginBottom: '0.25rem' }}>
                {errorName}:
              </strong>
            )}
            <span>{errorMessage}</span>
          </div>

          {/* Action Buttons */}
          {(retryable || dismissible) && (
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              flexWrap: 'wrap' as const 
            }}>
              {retryable && onRetry && (
                <button
                  onClick={handleRetry}
                  disabled={isRetrying}
                  style={{
                    padding: '0.375rem 0.75rem',
                    backgroundColor: typeStyles.iconColor,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    cursor: isRetrying ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    opacity: isRetrying ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  {isRetrying && (
                    <div style={{
                      width: '12px',
                      height: '12px',
                      border: '2px solid transparent',
                      borderTop: '2px solid currentColor',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  )}
                  {isRetrying ? 'Retrying...' : retryText}
                </button>
              )}

              {dismissible && (
                <button
                  onClick={handleDismiss}
                  style={{
                    padding: '0.375rem 0.75rem',
                    backgroundColor: 'transparent',
                    color: typeStyles.color,
                    border: `1px solid ${typeStyles.borderColor}`,
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {dismissText}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Close Button */}
        {dismissible && (
          <button
            onClick={handleDismiss}
            style={{
              background: 'none',
              border: 'none',
              color: typeStyles.color,
              cursor: 'pointer',
              padding: '0.25rem',
              borderRadius: '2px',
              flexShrink: 0
            }}
            aria-label="Close notification"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </button>
        )}
      </div>

      {/* CSS Animation for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ErrorNotification;
